import clsx from 'clsx';
import {
  Children,
  cloneElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useMemo,
  useRef,
} from 'react';
import type {
  ControlProps as ReactSelectControlProps,
  GroupBase,
} from 'react-select';

import { ChevronDownSmallIcon } from '@dynatrace/strato-icons';

import { HiddenSelect } from './HiddenSelect.js';
import { _isValueContainerComponent } from './ValueContainer.js';
import { Button } from '../../../buttons/button/Button.js';
import { useFocusRing } from '../../../core/hooks/useFocusRing.js';
import { useId } from '../../../core/hooks/useId.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useVisualRefreshEnabled } from '../../../core/hooks/useVisualRefreshEnabled.js';
import { OverlayStackingProvider } from '../../../core/providers/OverlayStackingProvider.js';
import { mergeProps as _mergeProps } from '../../../core/utils/merge-props.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { InternalBaseItem as BaseItem } from '../../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useSelectMenuContext } from '../hooks/use-select-menu-context.js';
import { SelectActions } from '../state/select-menu-state-reducer.js';
import * as styles from '../styles/Select.sty.js';

//#region Typings
type ControlProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectControlProps<OptionValue, isMulti, Group>;
//#endregion Typings

/** Iterates over child nodes to extract the value container. */
function getSelectValueContainer<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(children: ReactNode) {
  const childrenArray = Children.toArray(children);

  for (const child of childrenArray) {
    if (_isValueContainerComponent<OptionValue, isMulti, Group>(child)) {
      return child;
    }
  }

  // Assertion to control the return type, we should always get a valueContainer.
  throw new Error('Assertion to make sure the container exists.');
}

export function Control<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: Readonly<ControlProps<OptionValue, isMulti, Group>>) {
  const { children, isDisabled, innerRef, innerProps, selectProps } = props;
  const ariaLabelledBy = selectProps['aria-labelledby'];
  const {
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    readOnly,
    ariaDisabled,
    ...remainingProps
  } = selectProps.componentProps.trigger ?? {};
  const rootRef = useMergeRefs<HTMLElement | null>([
    innerRef,
    selectProps.setTriggerRef,
  ]);

  const valueContainer = getSelectValueContainer(children);
  const baseId = useId();
  const triggerId = `strato-select-trigger${baseId}`;
  const customTriggerId = selectProps.customTrigger?.props?.id;

  const showError = selectProps.formControlShowError;
  const hint = selectProps.formControlHint;

  // with the visual refresh enabled, a disabled select should not receive critical coloring on error
  const isCritical = useVisualRefreshEnabled()
    ? showError && !isDisabled
    : showError;

  const hiddenReactSelectInput = Array.isArray(valueContainer.props.children)
    ? valueContainer.props.children[1]
    : null;
  const id = selectProps['selectInputId'];

  const hiddenInput = useMemo(
    () =>
      cloneElement(hiddenReactSelectInput, {
        ...hiddenReactSelectInput.props,
        key: 'hidden-react-select-input',
        id,
        autoComplete: 'off',
        'aria-haspopup': 'listbox',
        'aria-invalid': showError,
        'aria-labelledby': ariaLabelledBy ?? customTriggerId ?? triggerId,
        'aria-label': selectProps['aria-label'],
        'aria-describedby': selectProps['aria-describedby'],
        'aria-details': selectProps['aria-details'],
      }),
    [
      hiddenReactSelectInput,
      id,
      showError,
      ariaLabelledBy,
      customTriggerId,
      triggerId,
      selectProps,
    ],
  );

  const { focusProps, focusClassName } = useFocusRing({
    color: showError ? 'critical' : 'neutral',
    focusWithin: true,
    disabled: isDisabled,
  });

  const { dispatch } = useSelectMenuContext();
  const handledByTouchRef = useRef(false);

  const interactionProps = useMemo(() => {
    return {
      onTouchEnd: () => {
        // On mobile, touchend fires before the emulated click. We dispatch
        // SELECTFOCUS to toggle the menu, then focus the filter input on the
        // next animation frame.
        handledByTouchRef.current = true;
        dispatch({
          type: SelectActions.SELECTFOCUS,
        });
        requestAnimationFrame(() => {
          selectProps.filterRef?.current?.inputRef?.focus({
            preventScroll: true,
          });
        });
      },
      onClick: () => {
        // Skip the emulated click that follows a touchend on mobile.
        if (handledByTouchRef.current) {
          handledByTouchRef.current = false;
          return;
        }
        dispatch({
          type: SelectActions.SELECTFOCUS,
        });
      },

      onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
        if (
          (event.key === 'Enter' || event.key === ' ') &&
          !selectProps.isMulti
        ) {
          dispatch({
            type: SelectActions.CLOSE,
          });
        }
      },
    };
  }, [dispatch, selectProps.isMulti]);

  const customTrigger = useMemo(() => {
    if (selectProps.customTrigger) {
      /** React Select dummyInput is applied in the ValueContainers children in order to
       * handle / react to the inputs correctly. This one needs to be passed through in order for the
       * trigger to work correctly.
       */

      return cloneElement(
        selectProps.customTrigger,
        {
          key: 'custom-trigger',
          ref: rootRef,
          id: triggerId,
          ..._mergeProps(
            selectProps.customTrigger.props,
            innerProps,
            ...(isDisabled
              ? []
              : [
                  interactionProps,
                  {
                    onFocus: selectProps.onFocus,
                    onBlur: selectProps.onBlur,
                  },
                ]),
          ),
        },
        [selectProps.customTrigger.props?.children, hiddenInput],
      );
    }
  }, [
    hiddenInput,
    innerProps,
    interactionProps,
    rootRef,
    selectProps.customTrigger,
    selectProps.onFocus,
    selectProps.onBlur,
    triggerId,
  ]);

  if (customTrigger) {
    return <OverlayStackingProvider>{customTrigger}</OverlayStackingProvider>;
  }

  return (
    <Flex padding={0} flexDirection="column" gap={4}>
      <OverlayStackingProvider>
        <Button
          ref={rootRef}
          id={triggerId}
          textAlign="start"
          data-testid={dataTestId}
          disabled={isDisabled}
          aria-disabled={ariaDisabled}
          width={selectProps.triggerWidth}
          {...innerProps}
          variant="emphasized"
          {...(isDisabled ? undefined : interactionProps)}
          color={isCritical ? 'critical' : 'neutral'}
          className={clsx(
            'strato-components-select-control-trigger',
            styles.selectTrigger({
              fullWidth:
                selectProps.triggerWidth === 'full' ||
                selectProps.triggerWidth === '100%',
              showError,
              readOnly,
            }),
            focusClassName,
            consumerClassName,
          )}
          style={consumerStyle}
          as="div"
          {...focusProps}
          {...remainingProps}
        >
          {selectProps.prefix && (
            <Button.Prefix
              data-testid={selectProps.prefix.props['data-testid']}
              className={selectProps.prefix.props.className}
              style={selectProps.prefix.props.style}
            >
              {selectProps.prefix.props.children}
            </Button.Prefix>
          )}
          <Button.Label>
            {hiddenInput}
            {valueContainer}
          </Button.Label>
          <Button.Suffix>
            <div
              className={clsx(
                styles.selectTriggerIconWrapper({
                  isOverlayOpen: selectProps.menuIsOpen,
                  readOnly,
                }),
              )}
            >
              <ChevronDownSmallIcon />
            </div>
          </Button.Suffix>
        </Button>
      </OverlayStackingProvider>
      <HiddenSelect<OptionValue>
        ref={selectProps.hiddenSelectRef}
        required={selectProps.isRequired}
        selectedValues={selectProps.value}
        name={selectProps.hiddenSelectName}
        form={selectProps.hiddenSelectForm}
        disabled={isDisabled || readOnly}
        controlState={selectProps.controlState}
      />
      {hint && (
        <BaseItem
          id={selectProps.formControlHintProps.id}
          variant={showError ? 'error' : 'hint'}
          disabled={isDisabled || ariaDisabled}
        >
          {hint}
        </BaseItem>
      )}
    </Flex>
  );
}
